import React from "react"
import { graphql } from "gatsby"

// We're using Gutenberg so we need the block styles
// these are copied into this project due to a conflict in the postCSS
// version used by the Gatsby and @wordpress packages that causes build
// failures.
// @todo update this once @wordpress upgrades their postcss version
import "../css/@wordpress/block-library/build-style/style.css"
import "../css/@wordpress/block-library/build-style/theme.css"

import "../scss/main.scss"

import FeaturedImage from "../components/FeaturedImage"
import Headline from "../components/Headline"
import Layout from "../components/Layout"
import PageHead from "../components/PageHead"
import MobileSidebarNavigationContainer from "../components/MobileSidebarNavigationContainer"
import SidebarNavigationPhases from "../components/SidebarNavigationPhases"
import SidebarNavigationTopics from "../components/SidebarNavigationTopics"
import SocialShare from "../components/SocialShare"
import BackLink from "../components/BackLink"
import BlockIterator from "../components/BlockIterator"
import WPColumns from "../components/WPColumns"
import WPColumn from "../components/WPColumn"
import ForceVanityDomain from "../utilities/ForceVanityDomain"
import GetCitationContent from "../utilities/GetCitationContent"

export function Head({ data, ...props }) {
  const { featuredImage, languageCode, seo } = data.page
  const { metaDesc, title } = seo || {}

  return (
    <PageHead
      {...props}
      meta={{
        title: title,
        description: metaDesc,
        image: ForceVanityDomain(featuredImage?.node?.sourceUrl),
        languageCode,
      }}
    />
  )
}

const BlogPageTemplate = ({ data: { page }, location }) => {
  const featuredImage = {
      data: page.featuredImage?.node?.localFile?.childImageSharp
        ?.gatsbyImageData,
      alt: page.featuredImage?.node?.altText || ``,
      caption:
        page.customFields.heroTitle ||
        page.customFields.navigationTitle ||
        page.title ||
        page.featuredImage?.node?.caption ||
        ``,
    },
    metaTitle = page.seo.title,
    metaDesc = page.seo.metaDesc,
    languageCode = page.languageCode,
    translationUri = page.translations[0]?.uri

  return (
    <Layout
      location={location}
      languageCode={languageCode}
      translationUri={translationUri}
      className="layout-phase"
      citationContent={GetCitationContent(page.blocks)}
      ctaBlock={page.blocks.some(
        block => block.name === "lazyblock/resources-cta-block"
      )}
    >
      {featuredImage?.data && (
        <FeaturedImage featuredImage={featuredImage} location={location} />
      )}
      <MobileSidebarNavigationContainer title="In This Section">
        <SidebarNavigationPhases currentPageId={page.id} alwaysOpen={true} />
        <SidebarNavigationTopics currentPageId={page.id} alwaysOpen={true} />
      </MobileSidebarNavigationContainer>
      <SocialShare
        location={location}
        metaTitle={metaTitle}
        metaDesc={metaDesc}
      />
      <Headline
        classes="headline-medium-large"
        title={page.title}
        location={location}
      />

      <WPColumns
        block={{
          attributes: {
            isStackedOnMobile: true,
          },
        }}
      >
        <WPColumn
          block={{
            attributes: {
              width: "66.66%",
            },
          }}
          className="main-content"
        >
          <BlockIterator blocks={page.blocks} location={location} />
        </WPColumn>
        <WPColumn className="desktop-sidebar-navigation">
          <BackLink
            linkText={
              languageCode === "es"
                ? "Ver Todas Las Fases y Temas Del Viaje"
                : "View All Journey Phases & Topics"
            }
            linkHref={
              languageCode === "es"
                ? "/es/el-viaje-del-embarazo"
                : "/pregnancy-journey-topics"
            }
          />
          <SidebarNavigationPhases
            currentPageId={page.id}
            languageCode={languageCode}
          />
          <SidebarNavigationTopics
            currentPageId={page.id}
            languageCode={languageCode}
          />
        </WPColumn>
      </WPColumns>
    </Layout>
  )
}

export default BlogPageTemplate

export const pageQuery = graphql`
  query BlogPageById($id: String!) {
    page: wpPage(id: { eq: $id }) {
      id
      title
      date(formatString: "MMMM DD, YYYY")
      blocks
      content
      languageCode
      translations {
        uri
      }
      ...FeaturedImagePageFragment
      seo {
        title
        metaDesc
      }
      customFields {
        heroTitle
        navigationTitle
      }
    }
  }
`
